"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  useTournamentGameScores,
  useTournamentReplayMetrics,
} from "@/entities/replay-metrics";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { api } from "@/shared/lib/api";

import { PlayerProfile } from "./player-profile";
import type { ProfileGame } from "./profile-game-bars";

/**
 * KAN-876: контейнер поведенческого профиля (эпик KAN-804).
 *
 * Команды берутся из статистики турнира: метрики реплеев про команды ничего не знают,
 * а поиск в макете обещает искать «игрока или команду». Запрос тот же, что у таблицы
 * игроков, и с тем же ключом — ответ приходит из кэша, второго обращения к сети нет.
 */

/**
 * Ответ `/tournaments/{id}/player-stats`, урезанный до нужного здесь. Тип РУЧНОЙ по
 * той же причине, что и в таблице игроков: ручки нет в OpenAPI (KAN-873). Удаляется
 * целиком при типизации ручки, а не правится.
 */
interface PlayerTeamRow {
  userId: string;
  teamName?: string | null;
}

async function fetchPlayerStats(tournamentId: string) {
  const res = await api.get<PlayerTeamRow[]>(
    `/tournaments/${encodeURIComponent(tournamentId)}/player-stats`,
  );
  return res.data ?? [];
}

export function TournamentPlayerProfileSection({
  tournamentId,
  selectedUserId,
  onSelectPlayer,
}: {
  tournamentId: string;
  selectedUserId?: string | null;
  onSelectPlayer: (userId: string) => void;
}) {
  const { data: players } = useTournamentReplayMetrics(tournamentId);

  // Секция последняя на странице итогов, а ручка «по играм» — самый тяжёлый ответ
  // страницы (~1,7 МБ). Запрашиваем её на подходе к экрану, а не при открытии
  // (KAN-1166). Сразу — если игрок уже выбран (клик в таблице) или браузер без
  // IntersectionObserver: иначе секция не загрузилась бы никогда.
  const { ref: sectionRef, isIntersecting } = useIntersectionObserver({
    rootMargin: "800px 0px",
    freezeOnceVisible: true,
  });
  const noObserver =
    typeof window !== "undefined" && !("IntersectionObserver" in window);
  const gameScoresWanted = isIntersecting || Boolean(selectedUserId) || noObserver;

  // Балл за каждую игру — отдельная ручка: единица наблюдения там игра, а не игрок.
  const { data: gameRows } = useTournamentGameScores(tournamentId, {
    enabled: gameScoresWanted,
  });

  const { data: stats = [] } = useQuery({
    queryKey: ["tournaments", tournamentId, "player-stats"],
    queryFn: () => fetchPlayerStats(tournamentId),
    enabled: Boolean(tournamentId),
    staleTime: 30_000,
  });

  const teamNameByUserId = useMemo(() => {
    const map: Record<string, string | null | undefined> = {};
    for (const row of stats) map[row.userId] = row.teamName;
    return map;
  }, [stats]);

  const gamesByUserId = useMemo(() => {
    const map = new Map<string, ProfileGame[]>();

    for (const row of gameRows ?? []) {
      // Без userId строку не к кому отнести, без gameId — нечем ключевать список.
      // И то и другое означает срез не по игре, а такие строки эта ручка не отдаёт.
      if (!row.userId || row.gameId == null) continue;

      const list = map.get(row.userId) ?? [];
      list.push({
        gameId: String(row.gameId),
        score: row.glScore ?? null,
        champion: row.champion,
        win: row.win,
        // KAN-769: причины «игры с оговоркой». Игру не прячем — она сыграна и
        // входит в историю; столбик рисуется серым, а флаги объясняют почему.
        integrityFlags: row.integrityFlags,
      });
      map.set(row.userId, list);
    }

    // Порядок по номеру игры: столбики читаются как история турнира слева направо.
    for (const list of map.values()) {
      list.sort((a, b) => Number(a.gameId) - Number(b.gameId));
    }

    return map;
  }, [gameRows]);

  // Турнир без разобранных реплеев — секции нет совсем. Пустой профиль на месте самой
  // крупной части страницы читался бы как поломка.
  if (!players || players.length === 0) return null;

  return (
    <div ref={sectionRef}>
      <PlayerProfile
        players={players}
        teamNameByUserId={teamNameByUserId}
        selectedUserId={selectedUserId}
        onSelectPlayer={onSelectPlayer}
        gamesByUserId={gamesByUserId}
      />
    </div>
  );
}
